import React, { useState, useEffect, useRef } from 'react';
import './LoadingScreen.css';

const BOOT_MESSAGES = [
  'CHECKING MEMORY INTEGRITY...',
  'LOADING CORE PROTOCOLS...',
  'INITIALIZING DISPLAY DRIVERS...',
  'SCANNING NEURAL PATHWAYS...',
  'ESTABLISHING SECURE CONNECTION...',
  'LOADING USER PROFILE...',
];

const BAR_WIDTH = 38;

const LoadingScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [completedMessages, setCompletedMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const doneRef = useRef(false);

  // Typing effect — one character at a time
  useEffect(() => {
    if (messageIndex >= BOOT_MESSAGES.length) return;
    const msg = BOOT_MESSAGES[messageIndex];
    if (charIndex < msg.length) {
      const t = setTimeout(() => {
        setCurrentMessage(msg.slice(0, charIndex + 1));
        setCharIndex(c => c + 1);
      }, 10);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => {
        setCompletedMessages(prev => [...prev, msg]);
        setCurrentMessage('');
        setCharIndex(0);
        setMessageIndex(i => i + 1);
      }, 180);
      return () => clearTimeout(t);
    }
  }, [charIndex, messageIndex]);

  // Progress bar over 3800ms
  useEffect(() => {
    const duration = 3800;
    const interval = 40;
    const steps = duration / interval;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const p = Math.min(100, (step / steps) * 100);
      setProgress(p);
      if (p >= 100) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, []);

  // Fade out when progress hits 100
  useEffect(() => {
    if (progress >= 100 && !doneRef.current) {
      doneRef.current = true;
      const t = setTimeout(() => {
        setFading(true);
        setTimeout(() => onComplete(), 1400);
      }, 1400);
      return () => clearTimeout(t);
    }
  }, [progress, onComplete]);

  const filled = Math.round((progress / 100) * BAR_WIDTH);
  const empty = BAR_WIDTH - filled;
  const progressBar = `[${'='.repeat(filled)}${'-'.repeat(empty)}]`;

  return (
    <div className={`loading-screen${fading ? ' loading-fade-out' : ''}`}>
      <div className="loading-scanlines" />
      <div className="loading-content">
        <div className="loading-header">
          <div className="loading-title">IBRAHIM MOHAMMAD</div>
          <div className="loading-subtitle">PORTFOLIO OS v1.0.1</div>
          <div className="loading-sep" />
        </div>

        <div className="loading-boot-label">INITIALIZING SYSTEM...</div>

        <div className="loading-messages">
          {completedMessages.map((msg, i) => (
            <div key={i} className="loading-msg">
              {msg} <span className="msg-ok">[  OK  ]</span>
            </div>
          ))}
          {messageIndex < BOOT_MESSAGES.length && (
            <div className="loading-msg">
              {currentMessage}
              <span className="loading-cursor">_</span>
            </div>
          )}
        </div>

        <div className="loading-progress">
          <div className="loading-progress-bar">
            LOADING {progressBar} {Math.floor(progress)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;
